import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/Button';
import { Toast } from '../components/Toast';
import { AlertCircleIcon, EyeIcon, EyeOffIcon, HandshakeIcon, MailIcon, LockIcon } from 'lucide-react';
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    type: 'success' as 'success' | 'error' | 'info' | 'warning',
    message: ''
  });
  const {
    login,
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      redirectToDashboard(currentUser.type);
    }
  }, [currentUser]);
  const redirectToDashboard = (userType: string) => {
    switch (userType) {
      case 'brand':
        navigate('/dashboard/brand');
        break;
      case 'organizer':
        navigate('/dashboard/organizer');
        break;
      case 'admin':
      case 'super_admin':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Login successful! Redirecting...'
      });
      // The useEffect will handle redirect when currentUser updates
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col" data-id="element-2684">
      {/* Header */}
      <div className="p-6" data-id="element-2685">
        <Link to="/" className="inline-flex items-center space-x-2 group" data-id="element-2686">
          <div className="bg-blue-600 p-2 rounded-lg shadow-sm" data-id="element-2687">
            <HandshakeIcon className="h-5 w-5 text-white" data-id="element-2688" />
          </div>
          <span className="text-xl font-bold text-gray-900" data-id="element-2689">SponsrAI</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16" data-id="element-2690">
        <div className="w-full max-w-md" data-id="element-2691">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8" data-id="element-2692">
            <div className="text-center mb-8" data-id="element-2693">
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2694">
                Welcome back
              </h1>
              <p className="text-gray-500" data-id="element-2695">Sign in to your SponsrAI account</p>
            </div>

            {error && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start" data-id="element-2696">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2697" />
                <div data-id="element-2698">
                  <p className="text-sm text-red-700" data-id="element-2699">{error}</p>
                  <Link to="/login/help" className="text-sm text-red-600 hover:text-red-700 font-medium mt-1 inline-block" data-id="element-2700">
                    Need help signing in?
                  </Link>
                </div>
              </div>}

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2701">
              {/* Email */}
              <div data-id="element-2702">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2703">
                  Email address
                </label>
                <div className="relative" data-id="element-2704">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-2705">
                    <MailIcon className="h-5 w-5 text-gray-400" data-id="element-2706" />
                  </div>
                  <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" placeholder="you@example.com" data-id="element-2707" />
                </div>
              </div>

              {/* Password */}
              <div data-id="element-2708">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2709">
                  Password
                </label>
                <div className="relative" data-id="element-2710">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-2711">
                    <LockIcon className="h-5 w-5 text-gray-400" data-id="element-2712" />
                  </div>
                  <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-11 pr-12 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" placeholder="••••••••" data-id="element-2713" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600" data-id="element-2714">
                    {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-2715" /> : <EyeIcon className="h-5 w-5" data-id="element-2716" />}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between" data-id="element-2717">
                <label className="flex items-center cursor-pointer" data-id="element-2718">
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2719" />
                  <span className="ml-2 text-sm text-gray-600" data-id="element-2720">
                    Remember me
                  </span>
                </label>
                <Link to="/login/help" className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-2721">
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading} data-id="element-2722">
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-8" data-id="element-2723">
              <div className="absolute inset-0 flex items-center" data-id="element-2724">
                <div className="w-full border-t border-gray-200" data-id="element-2725" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-2726">
                <span className="px-4 bg-white text-gray-500" data-id="element-2727">
                  New to SponsrAI?
                </span>
              </div>
            </div>

            {/* Register Link */}
            <Link to="/register" className="block w-full text-center px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-2728">
              Create an account
            </Link>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-8" data-id="element-2729">
            By signing in, you agree to our{' '}
            <a href="/terms" className="text-blue-600 hover:text-blue-700" data-id="element-2730">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700" data-id="element-2731">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>

      <Toast type={toast.type} message={toast.message} duration={5000} onClose={() => setToast({
      ...toast,
      isVisible: false
    })} isVisible={toast.isVisible} data-id="element-2732" />
    </div>;
}